import { ComponentProps, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, ScrollView, useTheme, VStack } from 'native-base';
import { find, isEmpty, map } from 'lodash';
import moment from 'moment';

import { Modal } from '@pimm/base';
import {
  GetReportDataForSiteQueryParams,
  GetSummaryReportsForSiteQueryParams,
  ReportIdInputDto,
  StoreReportInputDto,
  UpdateEodReviewRequest,
} from '@pimm/services/lib/store-inspections';
import { UserBySiteKeyDto } from '@pimm/services/lib/sms-tenants';
import { FormUserPasskey } from '@app/features/store-core/component/form-user-passkey';
import { UpdateEodReview } from '@pimm/services/lib/store-inspections/services';
import { ResourceLoader } from '@app/components/shared';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { useUsersWithSiteKeys } from '@app/features/app';
import { ReviewVerifyReportsConsumer } from '../context/review-verify-reports.context';
import { useSimpleSite } from '../context/simple-site.context';
import { useGetSummaryReportsBySite } from '../hooks/get-summary-reports-for-site.hook';
import { useCombineStoreReports, WeekFocus } from '../hooks/combine-store-reports.hook';
import { ReviewVerifyConfirmation } from './review-verify-confirmation';
import { StoreReportTable } from './store-report-table';
import { StoreInspectionSummary } from './store-inspection-summary';

export type PinSetupModalType = 'pin' | 'confirmation';

type StoreReportsProps = Pick<ComponentProps<typeof StoreInspectionSummary>, 'icon' | 'title' | 'titleTranslations'> & {
  _container?: ComponentProps<typeof VStack>;
  generalManager?: { userId?: string; name?: string };
  isEditEnabled?: boolean;
  marketId?: number;
  module: number;
  week: WeekFocus;
  onRegisterDownload?: (fn: () => void) => void;
};

export const StoreReports = ({
  _container,
  generalManager,
  marketId,
  module: moduleType,
  week,
  onRegisterDownload,
  ..._props
}: StoreReportsProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const modalReport = useModalFocus<Partial<GetReportDataForSiteQueryParams>>();
  const [query, setQuery] = useState<GetSummaryReportsForSiteQueryParams>();
  const simpleSite = useSimpleSite(_ => _.site);
  const queryReports = useGetSummaryReportsBySite(query);
  const combinedReports = useCombineStoreReports(simpleSite, week, queryReports.data);
  const [modalType, setModalType] = useState<PinSetupModalType | null>(null);

  const dateNow = moment().format('YYYY-MM-DD');
  const tableData = simpleSite?.id ? combinedReports.data[simpleSite.id] : [];
  const isEditEnabled = _props.isEditEnabled && !!query?.startDate && query.startDate < dateNow;
  const isVerified = queryReports.isSuccess && tableData.every(_ => _.isReviewed);

  const usersWithSiteKey = useUsersWithSiteKeys(state => state.users);

  const user = usersWithSiteKey?.find(_ => _.id === generalManager?.userId);

  const updateEodReview = useMutation<any, Error, UpdateEodReviewRequest>({ mutationFn: UpdateEodReview });

  const handleDownload = () => {
    if (!simpleSite?.id) return;
    combinedReports.download.mutate(simpleSite.id);
  };

  // Register download callback to parent
  // this causes infinite loop, please check
  // useEffect(() => {
  //   onRegisterDownload?.(handleDownload);
  // }, [onRegisterDownload, simpleSite?.id, combinedReports.download]);

  const handleVerifyAll = (_callback: (_payload: ReportIdInputDto[]) => void) => () => {
    if (!simpleSite?.id) return;
    const inputs = tableData.reduce((_inputs: ReportIdInputDto[], _report: any) => {
      const input = {
        reportId: _report.reportId,
        scheduleType: _report.scheduleType,
        storeReports: _report.entries.reduce((_inputs: StoreReportInputDto[], _: any) => {
          if (!_.totalForms.value || !!_.eodReview.value) return _inputs;
          return [..._inputs, { date: _.date.value, storeReportId: _.id }];
        }, []),
      };
      if (input.storeReports.length === 0) return _inputs;
      return [..._inputs, input];
    }, [] as ReportIdInputDto[]);
    _callback(inputs);
  };

  const handleChangePin = ({ id: _userId }: Partial<UserBySiteKeyDto>) => {
    const queryCache = queryClient.getQueryCache();
    const queryGetUsersWithSiteKey = find(queryCache.getAll(), _ => _.queryKey[0] === 'GetUsersWithSiteKey');

    if (queryGetUsersWithSiteKey) {
      const queryKey = ['GetUsersWithSiteKey', queryGetUsersWithSiteKey?.queryKey[1]];
      queryClient.setQueryData<UserBySiteKeyDto[]>(queryKey, (_prevData): UserBySiteKeyDto[] =>
        map(_prevData, _user => (_user.id === _userId ? { ..._user, pinIsSet: true } : _user)),
      );
    }
  };

  useEffect(() => {
    if (!moduleType || !simpleSite?.id || !week?.startDate) return;
    const startDate = moment(week.startDate).format().slice(0, 10);
    const endDate = moment(week.endDate).format().slice(0, 10);
    setQuery(prev => {
      if (prev && prev.module === moduleType && prev.siteId === simpleSite.id && prev.startDate === startDate && prev.endDate === endDate) {
        return prev;
      }
      return { module: moduleType, siteId: simpleSite.id!, startDate, endDate };
    });
  }, [moduleType, simpleSite?.id, week?.startDate?.getTime(), week?.endDate?.getTime()]);

  return (
    <VStack position="relative" flex={1} alignItems="center" justifyContent="center" {..._container}>
      <ScrollView w="full" h="full" stickyHeaderIndices={[0]} bg="gray.25" _contentContainerStyle={{ display: 'flex' }}>
        <VStack space={3} pt={1} pl={4} pr={1} pb={4} w="full">
          {map(tableData, (_report: any) => (
            <Box key={_report.reportId} rounded="lg" shadow={0}>
              <StoreReportTable
                isEditEnabled={isEditEnabled}
                moduleType={moduleType}
                report={_report}
                week={week}
                onSelect={modalReport.setOpen}
              />
            </Box>
          ))}
        </VStack>
      </ScrollView>

      <ResourceLoader
        position="absolute"
        w="full"
        isEmpty={isEmpty(tableData)}
        isLoading={!!simpleSite?.id && queryReports.isLoading}
        emptyMessage={t('store_inspections:no_data_available')}
      />

      <Modal
        size="full"
        _content={{ rounded: 'xl', width: '96%', height: '95%', maxWidth: 1600, maxHeight: 800 }}
        hideClose
        noPadding
        isOpen={modalReport.isOpen}
        onClose={modalReport.setHide}
      >
        {!!week?.startDate && (
          <StoreInspectionSummary
            icon={_props.icon}
            title={_props.title}
            titleTranslations={_props.titleTranslations}
            isEditEnabled={isEditEnabled}
            moduleType={moduleType}
            queryParams={modalReport.payload}
            queryReports={queryReports}
            week={week}
            onClose={modalReport.setHide}
          />
        )}
      </Modal>

      <ReviewVerifyReportsConsumer>
        {({ isOpen, confirmVerify, startPinSession, isPinSessionValid, ...props }) => {
          const pinValid = isPinSessionValid();
          const activeType: PinSetupModalType | null = isOpen ? (pinValid ? 'confirmation' : 'pin') : null;

          const handleClose = () => props.setHide();

          return (
            <>
              <Modal size="md" _content={{ rounded: 'xl', p: 6, maxWidth: 320 }} isOpen={activeType === 'pin'} noPadding hideClose>
                <FormUserPasskey
                  siteId={simpleSite?.id}
                  user={user}
                  onChange={handleChangePin}
                  onClose={handleClose}
                  onSuccess={async (_success?: boolean) => {
                    if (_success) startPinSession(60_000);
                    confirmVerify(updateEodReview)(_success);
                    handleClose();
                  }}
                />
              </Modal>

              <Modal size="md" _content={{ rounded: '2xl', maxW: 400 }} isOpen={activeType === 'confirmation'} noPadding hideClose>
                <ReviewVerifyConfirmation
                  isAuthorized
                  onConfirm={updateEodReview => confirm => confirm ? confirmVerify(updateEodReview)(true) : handleClose()}
                />
              </Modal>
            </>
          );
        }}
      </ReviewVerifyReportsConsumer>
    </VStack>
  );
};
